import React from 'react';
import { Button } from "@/components/ui/button";
import { BarChart3 } from "lucide-react";

interface PortfolioBacktestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PortfolioBacktestModal: React.FC<PortfolioBacktestModalProps> = ({ isOpen, onClose }) => {
  const [modalPeriod, setModalPeriod] = React.useState<'15m' | '30m' | '1h' | '4h'>('15m');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in duration-200">
      <div className="bg-card border border-border w-full max-w-2xl max-h-[85vh] rounded-2xl flex flex-col overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="p-4 border-b border-border/50 flex justify-between items-center bg-muted/20">
          <h3 className="text-sm font-black flex items-center gap-1.5">
            <BarChart3 className="w-4 h-4 text-primary" />
            기존 28개 종목 포트폴리오 백테스트 결과 (30일 평균)
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 font-bold text-muted-foreground">닫기</Button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          <div className="bg-muted/50 p-3 rounded-xl border border-border/50 text-[10px] space-y-1">
            <p className="font-bold text-muted-foreground flex justify-between">
              <span>종목: <strong>28개</strong></span>
              <span>레버리지: <strong>20x</strong></span>
              <span>수수료: <strong>0.05%</strong></span>
              <span>초기자본: <strong>10,000,000원</strong></span>
            </p>
            <p className="text-[9px] text-muted-foreground/80 italic mt-0.5">
              ETH/SEI/MASK/NOT/SAND/TRX/ZEC/ZRO/XAUT/PEPE 제외 28개 DEFAULT_SCAN_SYMBOLS 대상. 30일(15m/30m/1h), 40일(4h). 총 672회 백테스트.
            </p>
          </div>

          <div className="flex gap-2">
            {(['15m', '30m', '1h', '4h'] as const).map((period) => (
              <button
                key={period}
                type="button"
                onClick={() => setModalPeriod(period)}
                className={`flex-1 py-1 text-[10px] font-bold rounded-md border transition-all ${modalPeriod === period
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background hover:bg-muted text-muted-foreground border-border'
                  }`}
              >
                {period === '15m' ? '15분봉' : period === '30m' ? '30분봉' : period === '1h' ? '1시간봉' : '4시간봉'}
              </button>
            ))}
          </div>

          <div className="border border-border/60 rounded-xl overflow-hidden">
            <table className="w-full text-left border-collapse text-[10px]">
              <thead>
                <tr className="bg-muted/40 border-b border-border font-bold text-muted-foreground">
                  <th className="p-2">순위 / 전략</th>
                  <th className="p-2 text-right">평균 수익률</th>
                  <th className="p-2 text-right">양성비율</th>
                  <th className="p-2 text-right">승률</th>
                  <th className="p-2 text-right">Sharpe</th>
                  <th className="p-2 text-right">거래수</th>
                  <th className="p-2 text-right">MDD</th>
                </tr>
              </thead>
              <tbody>
                {modalPeriod === '15m' && (
                  <>
                    <tr className="border-b border-border/40 bg-success/5 font-bold">
                      <td className="p-2">1. channel_trend2</td>
                      <td className="p-2 text-right text-success">+2.60%</td>
                      <td className="p-2 text-right">81.5%</td>
                      <td className="p-2 text-right">87.0%</td>
                      <td className="p-2 text-right text-success">+0.46</td>
                      <td className="p-2 text-right">96</td>
                      <td className="p-2 text-right text-danger">15.3%</td>
                    </tr>
                    <tr className="border-b border-border/40">
                      <td className="p-2">2. auto_signal</td>
                      <td className="p-2 text-right text-danger">-2.53%</td>
                      <td className="p-2 text-right">63.0%</td>
                      <td className="p-2 text-right">63.9%</td>
                      <td className="p-2 text-right text-success">+0.13</td>
                      <td className="p-2 text-right">294</td>
                      <td className="p-2 text-right text-danger">30.8%</td>
                    </tr>
                    <tr className="border-b border-border/40">
                      <td className="p-2">3. zerolag_macd</td>
                      <td className="p-2 text-right text-danger">-5.98%</td>
                      <td className="p-2 text-right">37.0%</td>
                      <td className="p-2 text-right">44.5%</td>
                      <td className="p-2 text-right text-danger">-0.28</td>
                      <td className="p-2 text-right">3,312</td>
                      <td className="p-2 text-right text-danger">21.3%</td>
                    </tr>
                    <tr>
                      <td className="p-2">4. macd_ema</td>
                      <td className="p-2 text-right text-danger">-9.91%</td>
                      <td className="p-2 text-right">29.6%</td>
                      <td className="p-2 text-right">36.9%</td>
                      <td className="p-2 text-right text-danger">-0.30</td>
                      <td className="p-2 text-right">2,920</td>
                      <td className="p-2 text-right text-danger">24.8%</td>
                    </tr>
                  </>
                )}
                {modalPeriod === '30m' && (
                  <>
                    <tr className="border-b border-border/40 bg-success/5 font-bold">
                      <td className="p-2">1. auto_signal</td>
                      <td className="p-2 text-right text-success">+1.52%</td>
                      <td className="p-2 text-right">59.3%</td>
                      <td className="p-2 text-right">65.9%</td>
                      <td className="p-2 text-right text-success">+0.11</td>
                      <td className="p-2 text-right">165</td>
                      <td className="p-2 text-right text-danger">25.4%</td>
                    </tr>
                    <tr className="border-b border-border/40 bg-success/5">
                      <td className="p-2">2. channel_trend2</td>
                      <td className="p-2 text-right text-success">+0.96%</td>
                      <td className="p-2 text-right">70.4%</td>
                      <td className="p-2 text-right">70.4%</td>
                      <td className="p-2 text-right text-success">+0.11</td>
                      <td className="p-2 text-right">54</td>
                      <td className="p-2 text-right text-danger">5.01%</td>
                    </tr>
                    <tr className="border-b border-border/40">
                      <td className="p-2">3. zerolag_macd</td>
                      <td className="p-2 text-right text-danger">-3.69%</td>
                      <td className="p-2 text-right">40.7%</td>
                      <td className="p-2 text-right">40.4%</td>
                      <td className="p-2 text-right text-danger">-0.32</td>
                      <td className="p-2 text-right">1,818</td>
                      <td className="p-2 text-right text-danger">19.1%</td>
                    </tr>
                    <tr>
                      <td className="p-2">4. macd_ema</td>
                      <td className="p-2 text-right text-danger">-9.40%</td>
                      <td className="p-2 text-right">33.3%</td>
                      <td className="p-2 text-right">35.8%</td>
                      <td className="p-2 text-right text-danger">-0.37</td>
                      <td className="p-2 text-right">1,534</td>
                      <td className="p-2 text-right text-danger">22.8%</td>
                    </tr>
                  </>
                )}
                {modalPeriod === '1h' && (
                  <>
                    <tr className="border-b border-border/40 bg-success/5 font-bold">
                      <td className="p-2">1. auto_signal</td>
                      <td className="p-2 text-right text-success">+1.49%</td>
                      <td className="p-2 text-right">55.6%</td>
                      <td className="p-2 text-right">62.0%</td>
                      <td className="p-2 text-right text-success">+0.06</td>
                      <td className="p-2 text-right">50</td>
                      <td className="p-2 text-right text-danger">7.83%</td>
                    </tr>
                    <tr className="border-b border-border/40 bg-success/5">
                      <td className="p-2">2. macd_ema</td>
                      <td className="p-2 text-right text-success">+0.08%</td>
                      <td className="p-2 text-right">48.1%</td>
                      <td className="p-2 text-right">38.4%</td>
                      <td className="p-2 text-right text-danger">-0.11</td>
                      <td className="p-2 text-right">770</td>
                      <td className="p-2 text-right text-danger">16.6%</td>
                    </tr>
                    <tr className="border-b border-border/40">
                      <td className="p-2">3. channel_trend2</td>
                      <td className="p-2 text-right text-danger">-3.95%</td>
                      <td className="p-2 text-right">70.4%</td>
                      <td className="p-2 text-right">70.4%</td>
                      <td className="p-2 text-right text-success">+0.57</td>
                      <td className="p-2 text-right">54</td>
                      <td className="p-2 text-right text-danger">25.6%</td>
                    </tr>
                    <tr>
                      <td className="p-2">4. zerolag_macd</td>
                      <td className="p-2 text-right text-danger">-6.76%</td>
                      <td className="p-2 text-right">33.3%</td>
                      <td className="p-2 text-right">40.9%</td>
                      <td className="p-2 text-right text-danger">-0.58</td>
                      <td className="p-2 text-right">900</td>
                      <td className="p-2 text-right text-danger">21.6%</td>
                    </tr>
                  </>
                )}
                {modalPeriod === '4h' && (
                  <>
                    <tr className="border-b border-border/40 bg-success/5 font-bold">
                      <td className="p-2">1. auto4_mtf_signal</td>
                      <td className="p-2 text-right text-success">+1.97%</td>
                      <td className="p-2 text-right">57.1%</td>
                      <td className="p-2 text-right">53.6%</td>
                      <td className="p-2 text-right text-danger">-0.27</td>
                      <td className="p-2 text-right">31</td>
                      <td className="p-2 text-right text-danger">5.08%</td>
                    </tr>
                    <tr className="border-b border-border/40 text-muted-foreground">
                      <td className="p-2">2. auto_signal</td>
                      <td className="p-2 text-right">-0.24%</td>
                      <td className="p-2 text-right">0.0%</td>
                      <td className="p-2 text-right">0.0%</td>
                      <td className="p-2 text-right">-4.17</td>
                      <td className="p-2 text-right">2</td>
                      <td className="p-2 text-right">0.24%</td>
                    </tr>
                    <tr className="border-b border-border/40">
                      <td className="p-2">3. macd_ema</td>
                      <td className="p-2 text-right text-danger">-8.58%</td>
                      <td className="p-2 text-right">29.6%</td>
                      <td className="p-2 text-right">29.8%</td>
                      <td className="p-2 text-right text-danger">-1.29</td>
                      <td className="p-2 text-right">220</td>
                      <td className="p-2 text-right text-danger">18.6%</td>
                    </tr>
                    <tr>
                      <td className="p-2">4. zerolag_macd</td>
                      <td className="p-2 text-right text-danger">-12.48%</td>
                      <td className="p-2 text-right">25.9%</td>
                      <td className="p-2 text-right">26.5%</td>
                      <td className="p-2 text-right text-danger">-1.57</td>
                      <td className="p-2 text-right">300</td>
                      <td className="p-2 text-right text-danger">21.3%</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>

          <div className="space-y-2">
            <h4 className="text-[11px] font-black text-muted-foreground uppercase px-1">최종 권장 등급 (포트폴리오)</h4>
            <div className="space-y-2 flex flex-col">
              <div className="p-2.5 bg-primary/10 border border-primary/20 rounded-xl flex gap-2.5 text-[10px]">
                <div className="w-5 h-5 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold shrink-0 mt-0.5">1</div>
                <div>
                  <p className="font-bold">channel_trend2 (15분봉)</p>
                  <p className="text-muted-foreground">평균 +2.60%, 81.5% 양성, Sharpe 0.46, 27/28 심볼 거래. 포트폴리오 전체에서 가장 일관된 성과를 보인 유일한 전략.</p>
                </div>
              </div>
              <div className="p-2.5 bg-muted border border-border/50 rounded-xl flex gap-2.5 text-[10px]">
                <div className="w-5 h-5 rounded-full bg-muted/80 text-foreground flex items-center justify-center font-bold shrink-0 mt-0.5">2</div>
                <div>
                  <p className="font-bold">auto_signal (30분봉 / 1시간봉)</p>
                  <p className="text-muted-foreground">30m 평균 +1.52%, 1h 평균 +1.49%. NEAR +94.89%, TAO +28.67% 등 특정 심볼에서 폭발적 수익. 15m보다 상위 봉에서 안정적.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
